import { useState, useRef, useEffect, useMemo } from 'react';
import { X, Check, ChevronsUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { COUNTRIES, FREQUENT_JURISDICTIONS } from '@/data/countries';

interface JurisdictionMultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function JurisdictionMultiSelect({
  value,
  onValueChange,
  disabled = false,
  placeholder = "Select jurisdictions..."
}: JurisdictionMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return COUNTRIES;
    const term = search.toLowerCase();
    return COUNTRIES.filter(country => 
      country.toLowerCase().includes(term)
    );
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (country: string) => {
    if (value.includes(country)) {
      onValueChange(value.filter(v => v !== country));
    } else {
      onValueChange([...value, country]);
    }
    setSearch('');
    setOpen(false); // Закрываем выпадающий список после выбора
  };

  const handleRemove = (country: string) => {
    onValueChange(value.filter(v => v !== country));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !search && value.length > 0) {
      onValueChange(value.slice(0, -1));
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
    if (e.key === 'Enter' && filteredCountries.length > 0) {
      e.preventDefault();
      handleSelect(filteredCountries[0]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex flex-wrap gap-1.5 min-h-[42px] w-full rounded border border-input bg-background px-3.5 py-2.5 text-sm cursor-text ring-offset-background transition-all duration-150",
          disabled && "opacity-50 cursor-not-allowed",
          open && "outline-none border-ring ring-2 ring-ring/10"
        )}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        {value.map((country) => (
          <Badge
            key={country}
            variant="secondary"
            className="gap-1 pr-1"
          >
            {country}
            <button
              type="button"
              className="ml-1 rounded-full outline-none hover:bg-muted-foreground/20"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) handleRemove(country);
              }}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] bg-transparent outline-none border-none shadow-none focus:outline-none focus:ring-0 focus:border-none placeholder:text-muted-foreground"
          placeholder={value.length === 0 ? placeholder : ""}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 self-center ml-auto" />
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filteredCountries.length === 0 ? (
              <div className="py-2 px-3 text-sm text-muted-foreground">
                No countries found
              </div>
            ) : (
              (() => {
                const isSearching = search.trim().length > 0;
                const frequentSet = new Set(FREQUENT_JURISDICTIONS as readonly string[]);
                const frequentFiltered = isSearching ? [] : filteredCountries.filter(c => frequentSet.has(c));
                const otherFiltered = isSearching ? filteredCountries : filteredCountries.filter(c => !frequentSet.has(c));
                const renderItem = (country: string) => {
                  const isSelected = value.includes(country);
                  return (
                    <button
                      key={country}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                        isSelected && "bg-accent/50"
                      )}
                      onClick={() => handleSelect(country)}
                    >
                      <Check
                        className={cn(
                          "h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {country}
                    </button>
                  );
                };
                return (
                  <>
                    {!isSearching && frequentFiltered.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          Frequently used
                        </div>
                        {frequentFiltered.map(renderItem)}
                        <div className="my-1 border-t border-border/50" />
                        <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          All countries
                        </div>
                      </>
                    )}
                    {otherFiltered.map(renderItem)}
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}
